import { zodResolver } from '@hookform/resolvers/zod';
import { liteLevels, statuses } from 'constants/statuses';
import { Controller, useForm } from 'react-hook-form';
import {
  TextInput,
  Select,
  NumberInput,
  Switch,
  ColorInput,
  FileInput,
} from 'react-hook-form-mantine';
import * as M from '@mantine/core';
import { SCHOOL_ID_VALIDATOR } from 'utils/hasTextValidator';
import { generateSchoolId } from 'utils/generateSchoolId';
import { z } from 'zod';
import { useSaveSchool, useDistricts, SchoolListItem } from './queries';
import {
  schoolRegions,
  countryForRegion,
  schoolCountries,
  regionsForCountry,
} from 'constants/schoolRegions';
import {
  isValidLatitude,
  isValidLongitude,
  isValidTimezone,
} from 'utils/timezones';
import { SchoolTimezoneError } from 'utils/schoolTimezone';
import { SchoolTimezoneInput } from './SchoolTimezoneInput';
import { SchoolLocationAutofill } from './SchoolLocationAutofill';
import {
  COORDINATE_PAIR_ERROR,
  isEmptyLocationValue,
  missingLocationFields,
  parseCoordinatePair,
} from 'utils/schoolLocation';
import { ModalProps, rem } from '@mantine/core';
import { ClipboardEvent, useEffect, useRef, useState } from 'react';
import { IconUpload } from '@tabler/icons-react';
import { colors } from 'constants/colors';
import { schoolSaveErrorMessage } from './saveSchool';

export const SchoolForm = ({
  modalProps,
  schoolToEdit,
  createCollege = false,
  onSaved,
  inline = false,
}: {
  modalProps: ModalProps;
  /** undefined == mode "create" */
  schoolToEdit?: SchoolListItem;
  createCollege?: boolean;
  onSaved?: () => void;
  inline?: boolean;
}) => {
  const { data: districts } = useDistricts();
  const isCollege =
    createCollege || schoolToEdit?.organizationType === 'college';
  const districtOptions = [...(districts ?? [])];
  // Keep a saved district available while the lookup loads or if it omits it.
  if (
    schoolToEdit?.districtCode &&
    !districtOptions.some((d) => d.districtCode === schoolToEdit.districtCode)
  ) {
    districtOptions.push({
      districtCode: schoolToEdit.districtCode,
      districtName: schoolToEdit.districtName || schoolToEdit.districtCode,
    });
  }

  const schoolSchema = z
    .object({
      schoolName: z
        .string()
        .refine((val) => SCHOOL_ID_VALIDATOR(val), 'Enter a valid school code'),
      schoolTitle: z.string().min(1, 'Enter a school title'),
      nickname: createCollege
        ? z.string().trim().min(1, 'Enter a nickname')
        : z.string().optional(),
      website: z.union([z.string().url(), z.literal('')]).optional(),
      ncesCode: z.string().optional(),
      ipedsId: z.string().optional(),
      private: z.boolean().default(false),
      status: schoolToEdit
        ? isCollege
          ? z.enum(['live', 'suspended'])
          : z.enum(['live', 'waitlist', 'suspended'])
        : z.enum(['live', 'waitlist']),
      logo: z.instanceof(File).nullish(),
      primaryColor: z.string(),
      liteLevel: z.enum(['L1', 'L2', 'L3']).optional(),

      country: z.enum(['US', 'CA']),
      state: z
        .string()
        .refine(
          (value) => !!schoolRegions[value],
          'Select a state or province',
        ),
      districtCode: z
        .string()
        .optional()
        .refine((val) =>
          val ? districtOptions.some((d) => d.districtCode === val) : true,
        ),

      lat: z
        .number()
        .optional()
        .refine(isValidLatitude, 'Enter a valid latitude'),
      lng: z
        .number()
        .optional()
        .refine(isValidLongitude, 'Enter a valid longitude'),
      timezone: z.string().refine(isValidTimezone, 'Select a valid timezone'),
    })
    .refine((data) => countryForRegion(data.state) === data.country, {
      path: ['state'],
      message: 'Select a state or province in the selected country',
    });

  const {
    control,
    reset,
    handleSubmit,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { isSubmitting, dirtyFields },
  } = useForm<z.infer<typeof schoolSchema>>({
    resolver: zodResolver(schoolSchema, { async: true }),
    resetOptions: { keepValues: false },
    defaultValues: {
      schoolName: '',
      schoolTitle: '',
      country: 'US',
      status: createCollege ? 'live' : 'waitlist',
      primaryColor: '',
      private: false,
    },
    values: schoolToEdit
      ? ({
          ...schoolToEdit,
          logo: undefined,
          country: schoolToEdit.country ?? countryForRegion(schoolToEdit.state),
          timezone: schoolToEdit.timezone ?? '',
          lat: schoolToEdit.lat ?? undefined,
          lng: schoolToEdit.lng ?? undefined,
          nickname: schoolToEdit.nickname ?? '',
          website: schoolToEdit.website ?? '',
          ncesCode: schoolToEdit.ncesCode ?? undefined,
          ipedsId: schoolToEdit.ipedsId ?? undefined,
          districtCode: isCollege
            ? undefined
            : schoolToEdit.districtCode ?? undefined,
          status: schoolToEdit.status as z.infer<typeof schoolSchema>['status'],
          private: !!schoolToEdit.private,
          primaryColor: schoolToEdit.primaryColor ?? '',
          liteLevel: isCollege
            ? undefined
            : ((schoolToEdit.liteLevel ?? undefined) as z.infer<
                typeof schoolSchema
              >['liteLevel']),
        } satisfies z.infer<typeof schoolSchema>)
      : undefined,
  });

  const [saveError, setSaveError] = useState<string>();
  const saving = useRef(false);
  const [logoPreview, setLogoPreview] = useState<string>();
  const selectedLogo = watch('logo');
  useEffect(() => {
    if (!selectedLogo) {
      setLogoPreview(undefined);
      return;
    }
    const url = URL.createObjectURL(selectedLogo);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedLogo]);

  const [isIdEdited, setIsIdEdited] = useState(false);
  const [coordinatesPasted, setCoordinatesPasted] = useState(false);
  const countryIsDefault = useRef(
    !schoolToEdit?.country && !schoolToEdit?.state,
  );
  useEffect(() => {
    countryIsDefault.current = !schoolToEdit?.country && !schoolToEdit?.state;
  }, [schoolToEdit?.country, schoolToEdit?.state]);

  const pasteCoordinates =
    (field: 'lat' | 'lng') => (event: ClipboardEvent<HTMLInputElement>) => {
      const text = event.clipboardData.getData('text');
      if (!text.includes(',')) return;
      event.preventDefault();
      const coordinates = parseCoordinatePair(text);
      if (!coordinates) {
        setError(field, { message: COORDINATE_PAIR_ERROR });
        return;
      }
      // NumberInput keeps its focused text separate from externally set values.
      event.currentTarget.blur();
      setCoordinatesPasted(true);
      clearErrors(['lat', 'lng']);
      setValue('lat', coordinates.lat, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('lng', coordinates.lng, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };
  const schoolTitle = watch('schoolTitle');
  const state = watch('state');

  useEffect(() => {
    if (schoolToEdit || isIdEdited) return;
    const generatedId = generateSchoolId(
      schoolTitle,
      createCollege ? '' : state,
    );
    if (getValues('schoolName') === generatedId) return;
    setValue('schoolName', generatedId, { shouldValidate: true });
  }, [
    schoolTitle,
    state,
    createCollege,
    schoolToEdit,
    isIdEdited,
    getValues,
    setValue,
  ]);

  const saveSchoolMutation = useSaveSchool();

  const onClose = () => {
    if (saving.current) return;
    modalProps.onClose();
    setSaveError(undefined);
    if (schoolToEdit) reset();
    else
      reset({
        schoolName: '',
        schoolTitle: '',
        country: 'US',
        status: createCollege ? 'live' : 'waitlist',
        primaryColor: '',
        private: false,
      });
    setIsIdEdited(false);
    setCoordinatesPasted(false);
    countryIsDefault.current = !schoolToEdit?.country && !schoolToEdit?.state;
  };

  const onSubmit = async (formData: z.infer<typeof schoolSchema>) => {
    // Guard the mutation before React renders the disabled submit button.
    if (saving.current) return false;
    saving.current = true;
    setSaveError(undefined);
    try {
      const { logo, ...values } = formData;
      await saveSchoolMutation.mutateAsync({
        mode: schoolToEdit ? 'edit' : 'create',
        organizationType: isCollege ? 'college' : 'high_school',
        school: {
          ...values,
          // List/detail responses may omit this college-only field. Do not
          // clear a stored ID unless the operator actually edits the field.
          ipedsId:
            schoolToEdit && !dirtyFields.ipedsId
              ? schoolToEdit.ipedsId ?? undefined
              : values.ipedsId,
          alphabeticLunchWaves: schoolToEdit?.alphabeticLunchWaves,
        },
        logo: logo ?? undefined,
      });
      onSaved?.();
      return true;
    } catch (error) {
      if (error instanceof SchoolTimezoneError)
        setError('timezone', { message: error.message });
      else setSaveError(schoolSaveErrorMessage(error));
      return false;
    } finally {
      saving.current = false;
    }
  };

  const title = createCollege
    ? 'Create college'
    : schoolToEdit
    ? isCollege
      ? 'Edit college'
      : 'Edit school'
    : 'Create high school';

  const form = (
    <fieldset
      disabled={isSubmitting}
      style={{ border: 0, padding: 0, margin: 0 }}
    >
      <M.Stack>
        {saveError && (
          <M.Alert color="red" role="alert">
            {saveError}
          </M.Alert>
        )}
        {createCollege && (
          <M.Alert color="green">
            The college will be live as soon as it is created.
          </M.Alert>
        )}
        <M.Paper
          component="section"
          role="group"
          aria-label={isCollege ? 'College details' : 'School details'}
          withBorder
          radius="md"
          p="md"
        >
          <M.Stack>
            <M.Title order={3} size="h5">
              {isCollege ? 'College details' : 'School details'}
            </M.Title>
            <M.SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
              <TextInput
                control={control}
                name="schoolTitle"
                label={isCollege ? 'College title' : 'School title'}
              />
              <Controller
                control={control}
                name="schoolName"
                render={({ field, fieldState }) => (
                  <M.TextInput
                    {...field}
                    label={isCollege ? 'College ID' : 'School ID'}
                    disabled={!!schoolToEdit}
                    error={fieldState.error?.message}
                    onChange={(event) => {
                      setIsIdEdited(true);
                      field.onChange(event);
                    }}
                  />
                )}
              />
              <TextInput control={control} name="nickname" label="Nickname" />
              <TextInput control={control} name="website" label="Website" />
              {isCollege ? (
                <TextInput
                  control={control}
                  name="ipedsId"
                  label="IPEDS / institution ID"
                />
              ) : (
                <TextInput
                  control={control}
                  name="ncesCode"
                  label="NCES Code"
                />
              )}
              {(!isCollege || schoolToEdit) && (
                <Select
                  control={control}
                  name="status"
                  label={isCollege ? 'College status' : 'School status'}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                  description={
                    schoolToEdit
                      ? 'Choose Archive to hide this organization from the live list.'
                      : undefined
                  }
                  data={statuses
                    .filter((s) =>
                      schoolToEdit
                        ? !isCollege || s.id !== 'waitlist'
                        : s.id !== 'suspended',
                    )
                    .map((s) => ({ value: s.id, label: s.name }))}
                />
              )}

              {schoolToEdit && !isCollege && (
                <Select
                  control={control}
                  name="liteLevel"
                  label="Lite level"
                  data={liteLevels.map((ll) => ({
                    value: ll.id,
                    label: ll.name,
                  }))}
                />
              )}
            </M.SimpleGrid>
            <Switch
              control={control}
              name="private"
              label={isCollege ? 'Private college' : 'Private school'}
            />
            <M.SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
              <M.Stack spacing="xs">
                <FileInput
                  control={control}
                  name="logo"
                  label="Upload logo"
                  accept="image/png,image/jpeg"
                  icon={<IconUpload size={rem(14)} />}
                  clearable
                />
                {(logoPreview || schoolToEdit?.logo?.resourceUrl) && (
                  <M.Image
                    src={logoPreview || schoolToEdit?.logo?.resourceUrl}
                    alt="School logo preview"
                    width={80}
                    height={80}
                    fit="contain"
                  />
                )}
              </M.Stack>

              <ColorInput
                control={control}
                name="primaryColor"
                label="Primary color"
                placeholder="Pick color"
                disallowInput
                rightSection={<></>}
                withPicker={false}
                swatches={Object.keys(colors)}
              />
            </M.SimpleGrid>
          </M.Stack>
        </M.Paper>

        <M.Paper
          component="section"
          role="group"
          aria-label="Location"
          withBorder
          radius="md"
          p="md"
        >
          <M.Stack>
            <M.Title order={3} size="h5">
              Location
            </M.Title>
            <M.SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
              <Controller
                name="country"
                control={control}
                render={({ field, fieldState }) => (
                  <M.Select
                    label="Country"
                    required
                    data={schoolCountries}
                    value={field.value}
                    error={fieldState.error?.message}
                    onChange={(country) => {
                      if (country === 'US' || country === 'CA')
                        countryIsDefault.current = false;
                      if (
                        (country !== 'US' && country !== 'CA') ||
                        country === field.value
                      )
                        return;
                      field.onChange(country);
                      setValue('state', '');
                      setValue('districtCode', undefined);
                      clearErrors(['state', 'districtCode']);
                    }}
                  />
                )}
              />

              <Select
                control={control}
                name="state"
                label="State / province"
                searchable
                data={regionsForCountry(watch('country'))}
              />
            </M.SimpleGrid>

            {!isCollege && watch('country') === 'US' && (
              <Select
                control={control}
                name="districtCode"
                label="District"
                searchable
                limit={50}
                data={districtOptions.map((d) => ({
                  value: d.districtCode,
                  label: d.districtName,
                }))}
              />
            )}

            <M.SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
              <NumberInput
                control={control}
                name="lat"
                label="Latitude"
                placeholder="Latitude or latitude, longitude"
                onPaste={pasteCoordinates('lat')}
                precision={14}
                removeTrailingZeros
                min={-90}
                max={90}
                required
              />
              <NumberInput
                control={control}
                name="lng"
                label="Longitude"
                placeholder="Longitude or latitude, longitude"
                onPaste={pasteCoordinates('lng')}
                precision={14}
                removeTrailingZeros
                min={-180}
                max={180}
                required
              />
            </M.SimpleGrid>
            <SchoolLocationAutofill
              latitude={watch('lat')}
              longitude={watch('lng')}
              enabled={
                (inline || modalProps.opened) &&
                !isSubmitting &&
                (!watch('state') || !watch('country'))
              }
              onResolved={(location) => {
                const current = getValues();
                const updates = missingLocationFields(
                  { country: current.country, state: current.state },
                  location,
                  countryIsDefault.current,
                );
                if (updates.country) {
                  setValue('country', updates.country, {
                    shouldValidate: true,
                  });
                  countryIsDefault.current = false;
                }
                if (updates.state && isEmptyLocationValue(current.state))
                  setValue('state', updates.state, { shouldValidate: true });
              }}
            />
            <Controller
              name="timezone"
              control={control}
              render={({ field, fieldState }) => (
                <SchoolTimezoneInput
                  autofillOnlyIfEmpty={coordinatesPasted}
                  latitude={watch('lat')}
                  longitude={watch('lng')}
                  value={field.value}
                  onChange={(value) => {
                    clearErrors('timezone');
                    field.onChange(value);
                  }}
                  error={fieldState.error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </M.Stack>
        </M.Paper>

        <M.Flex justify="flex-end" mt="xl" gap="sm">
          <M.Button onClick={onClose} variant="outline">
            Cancel
          </M.Button>
          <M.Button
            onClick={handleSubmit(async (formData) => {
              if (await onSubmit(formData)) onClose();
            })}
            type="submit"
            loading={isSubmitting || saveSchoolMutation.isLoading}
          >
            {schoolToEdit
              ? 'Save'
              : createCollege
              ? 'Create college'
              : 'Create high school'}
          </M.Button>
        </M.Flex>
      </M.Stack>
    </fieldset>
  );

  if (inline) {
    return (
      <M.Container size="lg" py="xl">
        <M.Title order={2} mb="lg">
          {title}
        </M.Title>
        {form}
      </M.Container>
    );
  }

  return (
    <M.Modal
      {...modalProps}
      onClose={onClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
      title={title}
      size="xl"
    >
      {form}
    </M.Modal>
  );
};
